import React, { useState, useCallback } from 'react';
import {
  ArrowRight, ArrowLeft, Sparkles, Wand2, Target, Calendar as CalendarIcon,
  Package, Check, Settings2, Loader2, ChevronDown, Zap, LayoutGrid, Plus, X
} from 'lucide-react';
import { Button } from '../ui/button';
import { ANGLE_LABELS } from '../../data/pinTemplates';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuth = () => ({});

const GOAL_OPTIONS = [
  { id: 'drive_traffic', label: 'Drive trafikk', desc: 'Drive people to the website', icon: '→' },
  { id: 'promote_product', label: 'Promoter product', desc: 'Show frem og selg', icon: '★' },
  { id: 'build_awareness', label: 'Bygg visiblehet', desc: 'Reach new people', icon: '◎' },
  { id: 'get_saves', label: 'Get saves', desc: 'Engasjement og lagringer', icon: '♡' },
  { id: 'educational_growth', label: 'Edukativt innhold', desc: 'Voks med kunnskap', icon: '✦' },
];

const COUNT_OPTIONS = [3, 5, 10, 15];

const CampaignSetup = ({ brandKit, products: existingProducts, onCampaignCreated }) => {
  const [mode, setMode] = useState(null); // null | 'quick' | 'advanced'
  const [step, setStep] = useState('mode'); // mode | products | config | creating
  const [name, setName] = useState('');
  const [goal, setGoal] = useState('build_awareness');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [manualProduct, setManualProduct] = useState({ title: '', description: '', category: '', keywords: '', destinationUrl: '' });
  const [pinsPerProduct, setPinsPerProduct] = useState(5);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedAngles, setSelectedAngles] = useState([]);
  const [boards] = useState([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [showAddManual, setShowAddManual] = useState(false);

  const addManualProduct = () => {
    if (!manualProduct.title.trim()) return;
    setSelectedProducts(prev => [...prev, { ...manualProduct, id: `manual-${Date.now()}` }]);
    setManualProduct({ title: '', description: '', category: '', keywords: '', destinationUrl: '' });
    setShowAddManual(false);
  };

  const removeProduct = (idx) => {
    setSelectedProducts(prev => prev.filter((_, i) => i !== idx));
  };

  const toggleAngle = (angle) => {
    setSelectedAngles(prev =>
      prev.includes(angle) ? prev.filter(a => a !== angle) : [...prev, angle]
    );
  };

  const handleCreate = async () => {
    if (selectedProducts.length === 0) { setError('Add minst ett product'); return; }

    const campaignName = name.trim() || `${selectedProducts[0].title} Kampanje`;

    // Default dates if not set
    const now = new Date();
    const defaultStart = new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString();
    const defaultEnd = new Date(now.getTime() + 15 * 24 * 60 * 60 * 1000).toISOString();

    setCreating(true);
    setError('');
    setStep('creating');

    try {
      const res = await axios.post(`${API}/api/campaigns`, {
        name: campaignName,
        goal,
        products: selectedProducts.map(p => ({
          id: p.id || '',
          title: p.title,
          description: p.description || p.sub || '',
          category: p.category || p.type || '',
          keywords: p.keywords || '',
          destinationUrl: p.destinationUrl || p.url || '',
          imageUrl: p.imageUrl || '',
        })),
        pins_per_product: pinsPerProduct,
        start_date: startDate || defaultStart,
        end_date: endDate || defaultEnd,
        boards,
        brand_kit_snapshot: brandKit?.brand_name ? brandKit : null,
        marketing_angles: mode === 'advanced' ? selectedAngles : [],
        mode,
      }, getAuth());

      // Now trigger AI generation
      const genRes = await axios.post(
        `${API}/api/campaigns/${res.data.id}/generate`,
        {},
        getAuth()
      );

      onCampaignCreated(res.data.id, genRes.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Something went wrong during creation');
      setStep('config');
    }
    setCreating(false);
  };

  // Step: Mode selection
  if (step === 'mode') {
    return (
      <div className="space-y-6" data-testid="campaign-setup-mode">
        <div>
          <h2 className="font-serif font-bold text-stone-800 text-xl">Ny Pinterest-kampanje</h2>
          <p className="font-sans text-xs text-stone-400 mt-1">
            Lag en komplett Pinterest-strategi med smart variasjon og automatisk planlegging.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {/* Quick mode */}
          <button
            onClick={() => { setMode('quick'); setStep('products'); }}
            data-testid="campaign-mode-quick"
            className="bg-white rounded-2xl border-2 border-stone-100 p-6 text-left hover:border-[#E60023]/30 hover:shadow-md transition-all group"
          >
            <div className="w-12 h-12 rounded-2xl bg-[#E60023]/10 flex items-center justify-center mb-4">
              <Sparkles className="w-6 h-6 text-[#E60023]" />
            </div>
            <h3 className="font-serif font-bold text-stone-800 text-lg mb-1">Create campaign for me</h3>
            <p className="font-sans text-xs text-stone-500 leading-relaxed">
              Lumera automatically selects a good mix of designs, angles, copy and relevant boards. You just need to review.
            </p>
            <div className="mt-4 flex items-center gap-1.5 text-[#E60023] font-sans text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
              <Wand2 className="w-3.5 h-3.5" /> Anbefalt
            </div>
          </button>

          {/* Advanced mode */}
          <button
            onClick={() => { setMode('advanced'); setStep('products'); }}
            data-testid="campaign-mode-advanced"
            className="bg-white rounded-2xl border-2 border-stone-100 p-6 text-left hover:border-stone-300 hover:shadow-md transition-all group"
          >
            <div className="w-12 h-12 rounded-2xl bg-stone-100 flex items-center justify-center mb-4">
              <Settings2 className="w-6 h-6 text-stone-600" />
            </div>
            <h3 className="font-serif font-bold text-stone-800 text-lg mb-1">Full kontroll</h3>
            <p className="font-sans text-xs text-stone-500 leading-relaxed">
              Choose marketing angles, content mix, tidsplan og boards selv. For those who want to control everything.
            </p>
            <div className="mt-4 flex items-center gap-1.5 text-stone-500 font-sans text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
              <Settings2 className="w-3.5 h-3.5" /> Avansert
            </div>
          </button>
        </div>
      </div>
    );
  }

  // Step: Product selection
  if (step === 'products') {
    return (
      <div className="space-y-6" data-testid="campaign-setup-products">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-serif font-bold text-stone-800 text-xl">Choose products</h2>
            <p className="font-sans text-xs text-stone-400 mt-1">
              Add productene du vil lage Pinterest-kampanje for.
            </p>
          </div>
          <span className="text-[10px] font-sans font-bold text-[#E60023] bg-[#E60023]/10 px-2.5 py-1 rounded-full">
            {mode === 'quick' ? 'Quick Mode' : 'Avansert'}
          </span>
        </div>

        {/* Selected products */}
        {selectedProducts.length > 0 && (
          <div className="space-y-2">
            {selectedProducts.map((p, i) => (
              <div key={i} className="bg-white rounded-xl border border-stone-100 p-4 flex items-center gap-3" data-testid={`selected-product-${i}`}>
                <div className="w-10 h-10 rounded-lg bg-stone-50 flex items-center justify-center flex-shrink-0">
                  <Package className="w-4 h-4 text-stone-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-sans font-semibold text-stone-700 text-sm truncate">{p.title}</p>
                  {p.category && <p className="font-sans text-[10px] text-stone-400">{p.category}</p>}
                </div>
                <button onClick={() => removeProduct(i)} className="p-1.5 rounded-lg hover:bg-red-50 text-stone-300 hover:text-red-500 transition-colors">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add product form */}
        {showAddManual ? (
          <div className="bg-white rounded-2xl border border-stone-100 p-5 space-y-4">
            <div>
              <label className="font-sans text-xs font-semibold text-stone-600 mb-1.5 block">Productnavn *</label>
              <input
                data-testid="manual-product-title"
                value={manualProduct.title}
                onChange={e => setManualProduct(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Wedding Planner, Budget Tracker..."
                className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 placeholder:text-stone-300 focus:outline-none focus:border-[#B49A88] transition-colors"
              />
            </div>
            <div>
              <label className="font-sans text-xs font-semibold text-stone-600 mb-1.5 block">Description</label>
              <textarea
                value={manualProduct.description}
                onChange={e => setManualProduct(p => ({ ...p, description: e.target.value }))}
                placeholder="Short description..."
                rows={2}
                className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 placeholder:text-stone-300 focus:outline-none focus:border-[#B49A88] transition-colors resize-none"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="font-sans text-xs font-semibold text-stone-600 mb-1.5 block">Kategori</label>
                <input
                  value={manualProduct.category}
                  onChange={e => setManualProduct(p => ({ ...p, category: e.target.value }))}
                  placeholder="Wedding, Finance..."
                  className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 placeholder:text-stone-300 focus:outline-none focus:border-[#B49A88] transition-colors"
                />
              </div>
              <div>
                <label className="font-sans text-xs font-semibold text-stone-600 mb-1.5 block">Destination URL</label>
                <input
                  value={manualProduct.destinationUrl}
                  onChange={e => setManualProduct(p => ({ ...p, destinationUrl: e.target.value }))}
                  placeholder="https://..."
                  className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 placeholder:text-stone-300 focus:outline-none focus:border-[#B49A88] transition-colors"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowAddManual(false)} className="rounded-xl px-4 py-2.5 text-xs font-sans font-semibold">
                Cancel
              </Button>
              <Button onClick={addManualProduct} disabled={!manualProduct.title.trim()} className="bg-stone-900 hover:bg-stone-800 text-white rounded-xl px-4 py-2.5 text-xs font-sans font-semibold disabled:opacity-40">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add
              </Button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowAddManual(true)}
            data-testid="add-product-btn"
            className="w-full bg-white rounded-2xl border-2 border-dashed border-stone-200 p-5 flex items-center justify-center gap-2 text-stone-400 hover:border-stone-300 hover:text-stone-500 transition-all"
          >
            <Plus className="w-4 h-4" />
            <span className="font-sans text-sm font-medium">Add product</span>
          </button>
        )}

        {error && <p className="font-sans text-xs text-red-500">{error}</p>}

        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setStep('mode')} className="rounded-xl px-5 py-4 text-xs font-sans font-semibold">
            <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back
          </Button>
          <Button
            data-testid="next-to-config-btn"
            onClick={() => {
              if (selectedProducts.length === 0) { setError('Add minst ett product'); return; }
              setError('');
              setStep('config');
            }}
            className="flex-1 bg-stone-900 hover:bg-stone-800 text-white rounded-xl py-4 font-sans font-semibold text-xs"
          >
            {mode === 'quick' ? 'Configure campaign' : 'Next: Settings'} <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        </div>
      </div>
    );
  }

  // Step: Configuration
  if (step === 'config') {
    const totalPins = selectedProducts.length * pinsPerProduct;
    return (
      <div className="space-y-6" data-testid="campaign-setup-config">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-serif font-bold text-stone-800 text-xl">
              {mode === 'quick' ? 'Kampanjedetaljer' : 'Advanced settings'}
            </h2>
            <p className="font-sans text-xs text-stone-400 mt-1">
              {selectedProducts.length} product{selectedProducts.length > 1 ? 'er' : ''} selected
            </p>
          </div>
          <div className="text-right">
            <p className="font-sans text-2xl font-bold text-stone-800">{totalPins}</p>
            <p className="font-sans text-[10px] text-stone-400">pins totalt</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-stone-100 p-6 space-y-5">
          {/* Campaign name */}
          <div>
            <label className="font-sans text-xs font-semibold text-stone-600 mb-1.5 block">Campaign name</label>
            <input
              data-testid="campaign-name-input"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={`${selectedProducts[0]?.title || 'Min'} Kampanje`}
              className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 placeholder:text-stone-300 focus:outline-none focus:border-[#B49A88] transition-colors"
            />
          </div>

          {/* Campaign goal */}
          <div>
            <label className="font-sans text-xs font-semibold text-stone-600 mb-2 block">Campaign goal</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {GOAL_OPTIONS.map(g => (
                <button
                  key={g.id}
                  data-testid={`goal-${g.id}`}
                  onClick={() => setGoal(g.id)}
                  className={`px-3 py-3 rounded-xl border text-left transition-all ${
                    goal === g.id
                      ? 'border-[#E60023]/30 bg-[#E60023]/5'
                      : 'border-stone-100 hover:border-stone-200 bg-white'
                  }`}
                >
                  <span className="text-sm mb-1 block">{g.icon}</span>
                  <p className="font-sans font-semibold text-stone-700 text-[11px]">{g.label}</p>
                  <p className="font-sans text-[9px] text-stone-400">{g.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Pins per product */}
          <div>
            <label className="font-sans text-xs font-semibold text-stone-600 mb-2 block">Pins per product</label>
            <div className="flex gap-2">
              {COUNT_OPTIONS.map(n => (
                <button
                  key={n}
                  data-testid={`ppp-${n}`}
                  onClick={() => setPinsPerProduct(n)}
                  className={`px-5 py-2.5 rounded-xl text-sm font-sans font-semibold transition-all ${
                    pinsPerProduct === n
                      ? 'bg-[#E60023] text-white shadow-sm'
                      : 'bg-stone-50 text-stone-600 hover:bg-stone-100'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <p className="font-sans text-[10px] text-stone-400 mt-1.5">
              {selectedProducts.length} product{selectedProducts.length > 1 ? 'er' : ''} × {pinsPerProduct} pins = <span className="font-bold text-stone-600">{totalPins} pins totalt</span>
            </p>
          </div>

          {/* Dates */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="font-sans text-xs font-semibold text-stone-600 mb-1.5 block">Start date (optional)</label>
              <input
                type="date"
                data-testid="campaign-start-date"
                value={startDate ? startDate.split('T')[0] : ''}
                onChange={e => setStartDate(e.target.value ? new Date(e.target.value).toISOString() : '')}
                className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 focus:outline-none focus:border-[#B49A88] transition-colors"
              />
            </div>
            <div>
              <label className="font-sans text-xs font-semibold text-stone-600 mb-1.5 block">Sluttdato (optional)</label>
              <input
                type="date"
                data-testid="campaign-end-date"
                value={endDate ? endDate.split('T')[0] : ''}
                onChange={e => setEndDate(e.target.value ? new Date(e.target.value).toISOString() : '')}
                className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm font-sans text-stone-700 focus:outline-none focus:border-[#B49A88] transition-colors"
              />
            </div>
          </div>

          {/* Advanced: Marketing angles */}
          {mode === 'advanced' && (
            <div>
              <label className="font-sans text-xs font-semibold text-stone-600 mb-2 block">
                Marketing angles (optional — leave empty for auto)
              </label>
              <div className="grid sm:grid-cols-2 gap-2">
                {Object.entries(ANGLE_LABELS).map(([key, val]) => {
                  const active = selectedAngles.includes(key);
                  return (
                    <button
                      key={key}
                      data-testid={`angle-${key}`}
                      onClick={() => toggleAngle(key)}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition-all text-left ${
                        active
                          ? 'border-[#E60023]/30 bg-[#E60023]/5'
                          : 'border-stone-100 hover:border-stone-200'
                      }`}
                    >
                      <div
                        className="w-6 h-6 rounded-md flex items-center justify-center text-[10px] flex-shrink-0"
                        style={{ background: val.color + '15', color: val.color }}
                      >
                        {active ? <Check className="w-3 h-3" /> : '●'}
                      </div>
                      <p className="font-sans font-medium text-stone-700 text-xs">{val.label}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Brand Kit indicator */}
          {brandKit?.brand_name && (
            <div className="flex items-center gap-3 p-3 rounded-xl bg-stone-50 border border-stone-100">
              <div className="w-8 h-8 rounded-lg bg-[#B49A88]/10 flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4 text-[#B49A88]" />
              </div>
              <div>
                <p className="font-sans font-semibold text-stone-700 text-xs">Brand Kit aktiv</p>
                <p className="font-sans text-[10px] text-stone-400">{brandKit.brand_name} — brukes automatisk</p>
              </div>
            </div>
          )}
        </div>

        {error && <p className="font-sans text-xs text-red-500">{error}</p>}

        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setStep('products')} className="rounded-xl px-5 py-4 text-xs font-sans font-semibold">
            <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back
          </Button>
          <Button
            data-testid="create-campaign-btn"
            onClick={handleCreate}
            disabled={creating}
            className="flex-1 bg-[#E60023] hover:bg-[#cc001f] text-white rounded-xl py-4 font-sans font-semibold text-xs disabled:opacity-50"
          >
            {creating ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Genererer {totalPins} pins...</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" /> Lag {totalPins} Pins</>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // Step: Creating (progress)
  return (
    <div className="flex flex-col items-center justify-center py-20 space-y-6" data-testid="campaign-creating">
      <div className="w-16 h-16 rounded-2xl bg-[#E60023]/10 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#E60023]" />
      </div>
      <div className="text-center">
        <h3 className="font-serif font-bold text-stone-800 text-lg">Lumera lager kampanjen din</h3>
        <p className="font-sans text-xs text-stone-400 mt-2 max-w-sm mx-auto leading-relaxed">
          AI genererer {selectedProducts.length * pinsPerProduct} unike pins med smart variasjon i design, hooks, vinkler og copy...
        </p>
      </div>
      <div className="flex items-center gap-2 text-[10px] font-sans text-stone-400">
        <div className="w-1.5 h-1.5 rounded-full bg-[#E60023] animate-pulse" />
        Dette kan ta 15-60 sekunder
      </div>
    </div>
  );
};

export default CampaignSetup;
